import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { adminApi } from './api.js';

/**
 * The roster is the only route into a survey and the denominator for "who hasn't responded",
 * so it is worth being explicit about what an upload will do before it happens.
 */
export function RosterPage() {
  const { vendorId = '' } = useParams();
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const [replaceExisting, setReplaceExisting] = useState(false);

  const vendors = useQuery({ queryKey: ['vendors'], queryFn: adminApi.vendors });
  const roster = useQuery({
    queryKey: ['roster', vendorId],
    queryFn: () => adminApi.roster(vendorId),
  });

  const emails = [
    ...new Set(
      text
        .split(/[\s,;]+/)
        .map((entry) => entry.trim().toLowerCase())
        .filter((entry) => entry.includes('@')),
    ),
  ];

  const upload = useMutation({
    mutationFn: () => adminApi.uploadRoster(vendorId, { emails, replaceExisting }),
    onSuccess: async () => {
      setText('');
      await queryClient.invalidateQueries({ queryKey: ['roster', vendorId] });
    },
  });

  const vendor = vendors.data?.find((candidate) => candidate.id === vendorId);
  const willRemove = replaceExisting
    ? (roster.data ?? []).filter((entry) => !emails.includes(entry.email))
    : [];

  return (
    <>
      <p>
        <Link to="/admin">← Back to surveys</Link>
      </p>
      <h2>Employee list: {vendor?.name ?? '…'}</h2>

      <h3>Current employees ({roster.data?.length ?? 0})</h3>
      {roster.isPending && <p>Loading…</p>}
      {roster.data && roster.data.length === 0 && (
        <p>Nobody on the employee list yet, so no one can sign in to this company’s surveys.</p>
      )}
      {roster.data && roster.data.length > 0 && (
        <ul className="roster">
          {roster.data.map((entry) => (
            <li key={entry.email}>{entry.email}</li>
          ))}
        </ul>
      )}

      <h3>Add or replace</h3>
      <label>
        Email addresses (paste a list; commas, spaces or new lines all work)
        <textarea
          rows={8}
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder={'dave@example.co.uk\npriya@example.co.uk'}
        />
      </label>

      <label className="inline">
        <input
          type="checkbox"
          checked={replaceExisting}
          onChange={(event) => setReplaceExisting(event.target.checked)}
        />
        Replace the whole employee list (remove anyone not in this list)
      </label>

      <p className="note">
        {emails.length} valid address{emails.length === 1 ? '' : 'es'} detected
        {willRemove.length > 0 && `, ${willRemove.length} existing will be removed`}.
      </p>

      {willRemove.length > 0 && (
        <div className="problems" role="alert">
          <strong>These people will be removed:</strong>
          <ul>
            {willRemove.map((entry) => (
              <li key={entry.email}>{entry.email}</li>
            ))}
          </ul>
          <p>
            Their existing responses stay in the results; they just will not be able to sign in or
            be counted as outstanding.
          </p>
        </div>
      )}

      <button
        type="button"
        onClick={() => upload.mutate()}
        disabled={emails.length === 0 || upload.isPending}
      >
        {upload.isPending
          ? 'Saving…'
          : replaceExisting
            ? 'Replace employee list'
            : 'Add to employee list'}
      </button>

      {upload.isError && <p role="alert">{upload.error.message}</p>}
      {upload.isSuccess && (
        <p>
          Added {upload.data.added}, removed {upload.data.removed}.
        </p>
      )}
    </>
  );
}
